import { FC } from 'react';
import { get } from 'lodash';
import { Input, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import merge from '~/utils/mergeWithUndefined';
import { FormItemInjectedProps } from '~/types/FormItemInjectedProps';
import { ApplicationPayload } from '~/types/ApplicationPayload';
import FormDrawer from '~/components/FormDrawer';
import { Form } from '~/components';
import { useCommonErrors, useValidator } from '~/controllers/validation';
import { useGetApplication, useGetPayload } from '~/controllers/wizard';
import { useGetDraft } from '~/controllers/draft';
import { Draft } from '~/types/Draft';
import { useOptionalFields } from '~/hooks/useOptionalFields';
import { getStagePayload } from '~/utils/getStagePayload';
import { FormItemContextProvider } from '~/components/Form/FormItemContext';
import { isReadOnly } from '~/utils/isReadOnly';
import PersonFormItems from '~/pages/ksa/application/managers/components/PersonFormItems';
import InputsGroupLayout from '~/layouts/InputsGroupLayout';
import { KsaShareholders } from '~/types/ksa/KsaShareholders';
import { KsaPerson } from '~/types/ksa/KsaPerson';
import { WithCurrentUserTag } from '~/components/CurrentUser';
import { isCurrentUserByEmail } from '~/utils/getDecodedJwt';
import { useMobileVerification } from '~/hooks/useMobileVerification';
import { stepName } from '~/pages/ksa/application/shareholders';
import { isEditButtonDisabled } from '~/components/views/ShareholdersMultiple/utils';
import { Application, Stage } from '~/types/Application';
import { hasAtLeastOneDeclinedField } from '~/utils/softDeclinesFields';

interface Props extends Partial<FormItemInjectedProps<KsaShareholders>> {
  uid: string;
  onClose: () => void;
  isCheckedAuthorizedSignatory?: boolean;
  isSelectedCompanyLicense?: boolean;
}

const IndividualEdit: FC<Props> = (props) => {
  const { uid, onClose, isCheckedAuthorizedSignatory, isSelectedCompanyLicense } = props;
  const { value, onChange } = props as FormItemInjectedProps<KsaShareholders>;
  const { t } = useTranslation('ksaApplication');
  const shareholdersFormState = { shareholders: value };
  const applicationQuery = useGetApplication();

  const currentIndex =
    shareholdersFormState.shareholders?.individuals?.findIndex((item) => item?.uid === uid) || 0;

  const mutateMobileVerification = useMobileVerification();

  const [, handleError] = useCommonErrors();

  const onFinish = (newValues: ApplicationPayload) => {
    const updatedData = merge(shareholdersFormState, newValues);
    const isAuthorizedSignatory =
      updatedData?.shareholders?.individuals[currentIndex]?.personalData?.isAuthorizedSignatory;
    const hasVerifiedPhoneNumber =
      shareholdersFormState?.shareholders?.individuals &&
      shareholdersFormState?.shareholders?.individuals[currentIndex]?.personalData?.phoneNumber;
    if (isAuthorizedSignatory && !hasVerifiedPhoneNumber) {
      const updatedPhoneNumber =
        updatedData?.shareholders?.individuals[currentIndex]?.personalData?.phoneNumber;
      mutateMobileVerification
        .mutateAsync({ uid, phoneNumber: updatedPhoneNumber })
        .then(() => {
          onChange?.(updatedData.shareholders);
          onClose();
        })
        .catch((error) => {
          const code = error?.response?.data?.code;
          if (
            code === 'MOBILE_VERIFICATION_INVALID_NUMBER' ||
            code === 'MOBILE_VERIFICATION_INVALID_ID'
          ) {
            handleError.onError([{ name: ['MOBILE'], errors: [] }]);
          }
          if (code === 'MOBILE_VERIFICATION_NOT_OWNER') {
            handleError.onError([{ name: ['MOBILE_NOT_OWNER'], errors: [] }]);
          }
        });
    } else {
      onChange?.(updatedData.shareholders);
      onClose();
    }
  };

  const shareholderEmail = shareholdersFormState.shareholders?.individuals?.find(
    (item) => item?.uid === uid
  )?.personalData?.email;

  const withContinueButton = !isEditButtonDisabled(
    applicationQuery.data,
    hasAnyDeclinedField(applicationQuery?.data)
  );

  const isCurrent = isCurrentUserByEmail(shareholderEmail);
  return (
    <FormDrawer
      id="individualEditDrawer"
      visible={!!uid}
      onClose={onClose}
      title={
        <WithCurrentUserTag showLabel={isCurrent}>{t('Individual Shareholder')}</WithCurrentUserTag>
      }
      formName={formName}
      withErrors
      withContinueButton={withContinueButton}
    >
      <IndividualForm
        index={currentIndex}
        onFinish={onFinish}
        initialValues={shareholdersFormState}
        isCheckedAuthorizedSignatory={isCheckedAuthorizedSignatory}
        isSelectedCompanyLicense={isSelectedCompanyLicense}
        isVerifiedPhone={
          !!(
            shareholdersFormState?.shareholders?.individuals &&
            shareholdersFormState?.shareholders?.individuals[currentIndex]?.personalData
              ?.phoneNumber
          )
        }
      />
    </FormDrawer>
  );
};

interface IndividualFormProps {
  index: number;
  onFinish: (values: ApplicationPayload) => any;
  initialValues?: any | undefined;
  isCheckedAuthorizedSignatory?: boolean;
  isSelectedCompanyLicense?: boolean;
  isVerifiedPhone: boolean;
}

const IndividualForm: FC<IndividualFormProps> = (props) => {
  const {
    onFinish,
    initialValues,
    isCheckedAuthorizedSignatory,
    isSelectedCompanyLicense,
    index,
    isVerifiedPhone,
  } = props;
  const { t } = useTranslation('ksaApplication');

  const [form] = Form.useForm<ApplicationPayload>();

  const validatorController = useValidator(form.getFieldsValue(), undefined, Stage.ksaApplication);
  const applicationQuery = useGetApplication();
  const payload = useGetPayload();
  const draftQuery = useGetDraft<Draft<any>>();

  const optionalFieldsController = useOptionalFields({ initialValues });

  const stagePayload = getStagePayload.wizard(applicationQuery.data);

  const nameArr = getIndividualsFormName(index);

  const individualFromProvided: KsaPerson | undefined = get(
    payload?.provided,
    getIndividualsFormName(index)
  );
  const individualFromApplication: KsaPerson | undefined = get(
    applicationQuery?.data?.payload,
    getIndividualsFormName(index)
  );

  return (
    <FormItemContextProvider
      value={{
        defaultValidator: validatorController.validator,
        applicationPayload: applicationQuery.data?.payload,
        draftPayload: draftQuery.data,
        optionalFields: optionalFieldsController.optionalFields || {},
        errorFields: applicationQuery.data?.payload?.errorInfo?.errors,
        enabledFields: stagePayload?.activeStep.errorInformation?.enabledFields,
        disabledByDefault: isReadOnly(applicationQuery.data),
        disabled: isReadOnly(applicationQuery.data),
      }}
    >
      <Form
        form={form}
        name={formName}
        onFinish={onFinish}
        onValuesChange={(_: any, newValues: any) => {
          optionalFieldsController.debouncedUpdate(newValues);
        }}
        initialValues={initialValues}
      >
        <PersonFormItems
          isCheckedAuthorizedSignatory={isCheckedAuthorizedSignatory}
          isSelectedCompanyLicense={isSelectedCompanyLicense}
          name={nameArr}
          form={form}
          hasPassport={!!individualFromProvided?.passport?.documentNumber}
          personalDocumentType={individualFromProvided?.personalDocument?.idType}
          hasVerifiedPhoneNumber={
            !!individualFromProvided?.personalData?.phoneNumber ||
            !!individualFromApplication?.personalData?.phoneNumber ||
            isVerifiedPhone
          }
          isSignedWithEmail={!!individualFromProvided?.personalData?.email}
        />
        <section>
          <Typography.Title level={5}>{t('Ownership share:').toUpperCase()}</Typography.Title>
          <InputsGroupLayout>
            <Form.Item
              label={t('Shares, %')}
              name={[...nameArr, 'ownershipShare', 'percent']}
              disabled
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={t('Shares count')}
              name={[...nameArr, 'ownershipShare', 'count']}
              disabled
            >
              <Input />
            </Form.Item>
          </InputsGroupLayout>
        </section>
      </Form>
    </FormItemContextProvider>
  );
};

const formName = 'individuals';
export const getIndividualsFormName = (index: number) => [stepName, formName, index];

export const hasAnyDeclinedField = (application: Application | undefined) =>
  hasAtLeastOneDeclinedField(application, stepName);

export default IndividualEdit;
